/* global google */
const GOOGLE_MAPS_API_KEY_PLACEHOLDER = 'YOUR_GOOGLE_MAPS_API_KEY';
const GOOGLE_MAPS_SCRIPT_ID = 'comos-google-maps-script';
const GOOGLE_MAPS_CALLBACK_NAME = '__comosGoogleMapsInit';
const GOOGLE_MAPS_LIBRARIES = ['places', 'marker'] as const;

let googleMapsLoaderPromise: Promise<typeof google.maps> | null = null;

const getGoogleMapsApiKey = (): string | null => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim();

  if (!apiKey || apiKey === GOOGLE_MAPS_API_KEY_PLACEHOLDER) {
    return null;
  }

  return apiKey;
};

const getGoogleMapsMapId = (): string | undefined => {
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID?.trim();
  return mapId || undefined;
};

const buildGoogleMapsScriptUrl = (apiKey: string): string => {
  const url = new URL('https://maps.googleapis.com/maps/api/js');
  url.searchParams.set('key', apiKey);
  url.searchParams.set('libraries', GOOGLE_MAPS_LIBRARIES.join(','));
  url.searchParams.set('loading', 'async');
  url.searchParams.set('callback', GOOGLE_MAPS_CALLBACK_NAME);
  url.searchParams.set('v', 'weekly');
  return url.toString();
};

/**
 * Returns the configured Google Maps API key unless the checked-in placeholder
 * is still being used.
 */
export const getConfiguredGoogleMapsApiKey = (): string | null => {
  return getGoogleMapsApiKey();
};

/**
 * Returns the optional configured Google Maps map ID.
 */
export const getConfiguredGoogleMapsMapId = (): string | undefined => {
  return getGoogleMapsMapId();
};

/**
 * Loads the Google Maps JavaScript API exactly once and resolves with the
 * browser `google.maps` namespace.
 */
export const loadGoogleMapsApi = async (): Promise<typeof google.maps> => {
  if (window.google?.maps?.Map) {
    return window.google.maps;
  }

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    throw new Error('Google Maps API key is not configured.');
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  googleMapsLoaderPromise = new Promise<typeof google.maps>(
    (resolve, reject) => {
      const existingScript = document.getElementById(
        GOOGLE_MAPS_SCRIPT_ID
      ) as HTMLScriptElement | null;

      const cleanup = () => {
        delete window[GOOGLE_MAPS_CALLBACK_NAME];
      };

      window[GOOGLE_MAPS_CALLBACK_NAME] = () => {
        cleanup();
        if (window.google?.maps) {
          resolve(window.google.maps);
          return;
        }

        reject(new Error('Google Maps API loaded without maps namespace.'));
      };

      if (existingScript) {
        existingScript.addEventListener('error', () => {
          cleanup();
          reject(new Error('Unable to load Google Maps API.'));
        });
        return;
      }

      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = buildGoogleMapsScriptUrl(apiKey);
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        cleanup();
        reject(new Error('Unable to load Google Maps API.'));
      };

      document.head.appendChild(script);
    }
  ).catch((error) => {
    googleMapsLoaderPromise = null;
    throw error;
  });

  return googleMapsLoaderPromise;
};

/**
 * Loads the Google Maps Places library through the modern importLibrary API.
 */
export const loadGoogleMapsPlacesLibrary =
  async (): Promise<google.maps.PlacesLibrary> => {
    const maps = await loadGoogleMapsApi();

    if (!maps.importLibrary) {
      throw new Error('Google Maps Places library is unavailable.');
    }

    return (await maps.importLibrary('places')) as google.maps.PlacesLibrary;
  };

/**
 * Loads the Google Maps Marker library through the modern importLibrary API.
 */
export const loadGoogleMapsMarkerLibrary =
  async (): Promise<google.maps.MarkerLibrary> => {
    const maps = await loadGoogleMapsApi();

    if (!maps.importLibrary) {
      throw new Error('Google Maps Marker library is unavailable.');
    }

    return (await maps.importLibrary('marker')) as google.maps.MarkerLibrary;
  };

declare global {
  interface Window {
    __comosGoogleMapsInit?: () => void;
  }
}
